import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Routes, RouterModule, Router } from '@angular/router';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { SwiperModule } from 'swiper/angular';
import { environment } from 'src/environments/environment';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSliderModule } from '@angular/material/slider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatBadgeModule } from '@angular/material/badge';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PlatformModule } from '@angular/cdk/platform';
import { MatRippleModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';

import { AppComponent } from './app.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { DataDeletionComponent } from './pages/data-deletion/data-deletion.component';
import { CompareResultsComponent } from './pages/compare-results/compare-results.component';
import { HeaderComponent } from './components/header/header.component';
import { RulesComponent } from './pages/rules/rules.component';
import { LandingComponent } from './pages/landing/landing.component';
import { OptionSearchComponent } from './components/option-search/option-search.component';
import { FeaturedPlatesComponent } from './components/featured-plates/featured-plates.component';
import { PlateComponentModule } from './components/plate/plate.module';
import { SearchResultTableModule } from './components/search-result-table/search-result-table.module';
import { AuctionsComponent } from './pages/auctions/auctions.component';
import { LiveAuctionsComponent } from './pages/live-auctions/live-auctions.component';
import { CreateAuctionComponent } from './pages/create-auction/create-auction.component';
import { PlateInputComponent } from './components/plate-input/plate-input.component';
import { RouterButtonComponent } from './components/router-button/router-button.component';
import { SearchResultsPageComponent } from './pages/search-results-page/search-results-page.component';
import { LiveV2ComparePageComponent } from './pages/live-v2-compare-page/live-v2-compare-page.component';
import { PlateModalComponent } from './components/plate-modal/plate-modal.component';
import { InfoComponent } from './pages/info/info.component';
import { AccountComponent } from './pages/account/account.component';
import { AccountV2Component } from './pages/account-v2/account-v2.component';
import { SavedGuestComponent } from './pages/saved-guest/saved-guest.component';
import { SavedSearchBuilderComponent } from './pages/saved-search/saved-search-builder.component';
import { SavedSearchResultsComponent } from './pages/saved-search/saved-search-results.component';
import { HomeV2Component } from './pages/home-v2/home-v2.component';
import { HomeSearchComponent } from './pages/home-v2/home-search/home-search.component';
import { HomeMarketComponent } from './pages/home-v2/home-market/home-market.component';
import { HomeInFocusComponent } from './pages/home-v2/home-in-focus/home-in-focus.component';
import { HomeRegistryComponent } from './pages/home-v2/home-registry/home-registry.component';
import { HomeForYouComponent } from './pages/home-v2/home-for-you/home-for-you.component';
import { AuctionAdminComponent } from './components/auction-admin/auction-admin.component';
import { AlertActionComponent } from './pages/alert-action/alert-action.component';
import { FooterComponent } from './components/footer/footer.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { RegisterComponent } from './pages/register/register.component';
import { ForgotPasswordPageComponent } from './pages/forgot-password-page/forgot-password-page.component';
import { ResetPasswordPageComponent } from './pages/reset-password-page/reset-password-page.component';
import { CompareResultTableComponent } from './components/compare-result-table/compare-result-table.component';
import { CheckoutCancelComponent } from './pages/checkout-cancel/checkout-cancel.component';
import { OrderConfirmationComponent } from './pages/order-confirmation/order-confirmation.component';
import { GlossaryDefPipe, GlossaryLinkPipe } from './pipes/GlossaryLinkPipe';

import { NoCacheHeadersInterceptor } from './http/no-cache-header';
import { UrlInterceptor } from './http/url-interceptor';
import { TokenInterceptor } from './http/token-interceptor';
import { AccountProfileInterceptor } from './http/account-profile-interceptor';
import { SessionService } from './services/session-service';
import { UserService } from './services/user-service';
import { BYOSearchPageComponent } from './pages/byo-search-page/byo-search-page.component';
import { ListingCardComponent } from './components/listing-card/listing-card.component';
import { CompareResultRowComponent } from './components/compare-result-row/compare-result-row.component';
import { WildcardCharacterSelectComponent } from './components/wildcard-character-select/wildcard-character-select.component';
import { TopBarComponent } from './components/top-bar/top-bar.component';
import { NavShellComponent } from './components/nav-shell/nav-shell.component';
import { AssistantWidgetComponent } from './components/assistant-widget/assistant-widget.component';
import { SearchBodyV2Component } from './components/search-body-v2/search-body-v2.component';
import { FeedbackModalComponent } from './components/feedback-modal/feedback-modal.component';
import { FeedbackDialogModalComponent } from './components/feedback-dialog-modal/feedback-dialog-modal.component';
import { SpecialOfferSectionComponent } from './components/special-offer-section/special-offer-section.component';
import { SearchingModalComponent } from './components/searching-modal/searching-modal.component';
import { AdminPageComponent } from './pages/admin-page/admin-page.component';
import { PlateBreakdownComponent } from './components/plate-breakdown/plate-breakdown.component';
import { InitialsSearchPageComponent } from './pages/initials-search-page/initials-search-page.component';
import { InitialSectionComponent } from './components/initial-section/initial-section.component';
import { PlatopediaPageComponent } from './pages/platopedia-page/platopedia-page.component';
import { PlatopediaListComponent } from './components/platopedia-list/platopedia-list.component';
import { MultiSearchPageComponent } from './pages/multi-search-page/multi-search-page.component';
import { NameListPageComponent } from './pages/name-list-page/name-list-page.component';
import { FavouritesListComponent } from './components/favourites-list/favourites-list.component';
import { ResultsHeaderComponent } from './components/results-header/results-header.component';
import { DatelessSearchPageComponent } from './pages/dateless-search-page/dateless-search-page.component';
import { PlateViewerComponent } from './components/plate-viewer/plate-viewer.component';
import { LargeImageModalComponent } from './components/large-image-modal/large-image-modal.component';
import { PlateSellerRowComponent } from './components/plate-seller-row/plate-seller-row.component';
import { PlateInfoPanelComponent } from './components/plate-info-panel/plate-info-panel.component';
import { PlateCheckoutStepsComponent } from './components/plate-checkout-steps/plate-checkout-steps.component';
import { NomineeFormComponent } from './components/nominee-form/nominee-form.component';
import { ChartComponent } from './components/chart/chart.component';
import { PlateOptionSelectionComponent } from './components/plate-option-selection/plate-option-selection.component';
import { NgChartsModule } from 'ng2-charts';
import { CompareInlineComponent } from './components/compare-inline/compare-inline.component';
import { QuickSignUpModalComponent } from './components/quick-sign-up-modal/quick-sign-up-modal.component';
import { RatingBarComponent } from './components/rating-bar/rating-bar.component';
import { SoldDataTableComponent } from './components/sold-data-table/sold-data-table.component';

import * as Sentry from '@sentry/angular';
import { PriceBreakdownComponent } from './components/price-breakdown/price-breakdown.component';
import { SearchVariantBarComponent } from './components/search-variant-bar/search-variant-bar.component';
import { BannerImageComponent } from './components/banner-image/banner-image.component';
import { SellingLandingPageComponent } from './pages/selling-landing-page/selling-landing-page.component';
import { TabbedSectionComponent } from './components/tabbed-section/tabbed-section.component';
import { SellersHubPageComponent } from './pages/sellers-hub-page/sellers-hub-page.component';
import { BuyPageComponent } from './pages/buy-page/buy-page.component';
import { MessengerPageComponent } from './pages/messenger-page/messenger-page.component';
import { MessengerListComponent } from './components/messenger-list/messenger-list.component';
import { MessengerClientComponent } from './components/messenger-client/messenger-client.component';
import { SellerTableComponent } from './componts/seller-table/seller-table.component';
import { PlatexDirectComponent } from './components/platex-direct/platex-direct.component';
import { ComparePageComponent } from './pages/compare-page/compare-page.component';
import { CompareResultsTableComponent } from './components/compare-results-table/compare-results-table.component';
import { RegistrationDetailPageComponent } from './pages/registration-detail-page/registration-detail-page.component';
import { ImageSliderComponent } from './components/image-slider/image-slider.component';
import { PaymentOptionsComponent } from './components/payment-options/payment-options.component';
import { PaypalComponent } from './components/payment/paypal/paypal.component';
import { PageStepsComponent } from './components/page-steps/page-steps.component';
import { HomePageSearchComponent } from './components/home-page-search/home-page-search.component';
import { ByoSearchInputComponent } from './byo-search-input/byo-search-input.component';
import { DvlaAuctionPageComponent } from './pages/dvla-auction-page/dvla-auction-page.component';
import { AccountDetailsComponent } from './pages/account-details/account-details.component';
import { AccountFavouritesComponent } from './pages/account-favourites/account-favourites.component';
import { AccountNotifyComponent } from './pages/account-notify/account-notify.component';
import { AccountAlertsComponent } from './pages/account-alerts/account-alerts.component';
import { AccountMessagesComponent } from './pages/account-messages/account-messages.component';
import { AccountSellingComponent } from './pages/account-selling/account-selling.component';
import { GlossaryComponent } from './components/glossary/glossary.component';
import { GlossaryTermWordComponent } from './glossary-term-word/glossary-term-word.component';
import { SwitchToggleComponent } from './components/switch-toggle/switch-toggle.component';
import { NamePlatePageComponent } from './pages/name-plate-page/name-plate-page.component';
import { TaxonomyShellComponent } from './pages/taxonomy-shell/taxonomy-shell.component';
import { SignupCaptureModalComponent } from './components/signup-capture-modal/signup-capture-modal.component';
import { FaqComponent } from './components/faq/faq.component';
import { SpecialPlateInfoComponent } from './components/special-plate-info/special-plate-info.component';
import { EditListingPageComponent } from './pages/edit-listing-page/edit-listing-page.component';
import { BillingListingPageComponent } from './pages/billing-listing-page/billing-listing-page.component';
import { TrackingChartComponent } from './components/tracking-chart/tracking-chart.component';
import { MetricGraphComponent } from './components/metric-graph/metric-graph.component';
import { PlateViewerPageComponent } from './pages/plate-viewer-page/plate-viewer-page.component';
import { BlogPageComponent } from './pages/blog-page/blog-page.component';
import { BlogLandingPageComponent } from './pages/blog-landing-page/blog-landing-page.component';
import { GuideLandingComponent } from './market-guides/guide-landing/guide-landing.component';
import { GuideReaderComponent } from './market-guides/guide-reader/guide-reader.component';
import { GuidesLibraryComponent } from './market-guides/guides-library/guides-library.component';
import { GuideFeatureComponent } from './market-guides/guide-feature/guide-feature.component';
import { WhatsOnYourPlateComponent } from './market-guides/whats-on-your-plate/whats-on-your-plate.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { OrdinalDatePipe } from './pipes/OrdinalDatePipe';
import { ImageCachePipe } from './pipes/ImageCachePipe';
import { OrdinalNumberPipe } from './pipes/OrdinalNumberPipe';
import { TpSellersComponent } from './components/tp-sellers/tp-sellers.component';
import { SaleDataComponent } from './components/sale-data/sale-data.component';
import { PriceHistoryComponent } from './components/price-history/price-history.component';
import { CompareLoaderComponent } from './components/compare-loader/compare-loader.component';
import { NgxMarqueeModule } from 'ngx-marquee';
import { SideBarFiltersComponent } from './components/side-bar-filters/side-bar-filters.component';
import { CustomDropdownComponent } from './components/custom-dropdown/custom-dropdown.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MarketInsightComponent } from './components/market-insight/market-insight.component';
import { DynamicChartsComponent } from './components/dynamic-charts/dynamic-charts.component';
import { UserHistoryComponent } from './components/user-history/user-history.component';
import { ChartPriceDataComponent } from './chart-price-data/chart-price-data.component';
import { NumberPlateTypesComponent } from './pages/articles/number-plate-types/number-plate-types.component';
import { PrivateNumberPlatesComponent } from './pages/articles/private-number-plates/private-number-plates.component';
import { CherishedNumberPlatesComponent } from './pages/articles/cherished-number-plates/cherished-number-plates.component';
import { DatelessNumberPlatesComponent } from './pages/articles/dateless-number-plates/dateless-number-plates.component';
import { PrefixNumberPlatesComponent } from './pages/articles/prefix-number-plates/prefix-number-plates.component';
import { SuffixNumberPlatesComponent } from './pages/articles/suffix-number-plates/suffix-number-plates.component';
import { HowToTransferARegistrationComponent } from './pages/articles/how-to-transfer-a-registration/how-to-transfer-a-registration.component';
import { HowToRetainARegistrationComponent } from './pages/articles/how-to-retain-a-registration/how-to-retain-a-registration.component';
import { HowToPurchaseARegistrationComponent } from './pages/articles/how-to-purchase-a-registration/how-to-purchase-a-registration.component';
import { HOME_DESCRIPTION, PAGE_META } from './page-meta';
import { BLOG_LANDING_DESCRIPTION, BLOG_LANDING_TITLE } from './utils/blog-meta';
import { GUIDES, GUIDES_HUB_DESCRIPTION, GUIDES_HUB_TITLE, guideTitle } from './article-landing/guides';
import { FeaturedBlogsComponent } from './featured-blogs/featured-blogs.component';
import { ArticleLandingComponent } from './article-landing/article-landing.component';
import { ChartPriceHistoryComponent } from './chart-price-history/chart-price-history.component';
import { NewReleasePageComponent } from './pages/new-release-page/new-release-page.component';
import { PagePlacementComponent } from './components/page-placement/page-placement.component';
import { NewReleaseMarketDataComponent } from './components/new-release-market-data/new-release-market-data.component';
import { NamePopularityDataComponent } from './components/name-popularity-data/name-popularity-data.component';
import { AdminGptComponent } from './pages/admin-gpt/admin-gpt.component';
import { AdminWordbankComponent } from './pages/admin-wordbank/admin-wordbank.component';
import { BuyLandingComponent } from './pages/buy-landing/buy-landing.component';
import { SellLandingComponent } from './pages/sell-landing/sell-landing.component';
import { FeaturedListingsComponent } from './featured-listings/featured-listings.component';
import { InViewportModule } from 'ng-in-viewport';
import { PriceHistoryChartComponent } from './price-history-chart/price-history-chart.component';
import { CustomLoaderComponent } from './components/custom-loader/custom-loader.component';
import { TextChangerComponent } from './components/text-changer/text-changer.component';
import { HowToSaveMoneyComponent } from './pages/articles/how-to-save-money/how-to-save-money.component';
import { SearchLoaderComponent } from './components/search-loader/search-loader.component';
import { NewSearchInputComponent } from './components/new-search-input/new-search-input.component';
import { PlateDetailPageComponent } from './pages/plate-detail-page/plate-detail-page.component';
import { MainNavChipsModule } from './components/main-nav-chips/main-nav-chips.module';
import { FooterPopoutComponent } from './components/footer-popout/footer-popout.component';
import { InitialsPopularityDataComponent } from './components/initials-popularity-data/initials-popularity-data.component';
import { InitialsPopularityTableComponent } from './components/initials-popularity-table/initials-popularity-table.component';
import { MarketShareComponent } from './components/market-share/market-share.component';
import { CodeInputModule } from 'angular-code-input';
import { UnderOneHundredComponent } from './pages/under-one-hundred/under-one-hundred.component';
import { Under200RegisterComponent } from './pages/under-200-register/under-200-register.component';
import { NumberOnePageComponent } from './pages/number-one-page/number-one-page.component';
import { NumberOnesRegisterComponent } from './pages/number-ones-register/number-ones-register.component';
import { NamesRegisterComponent } from './pages/names-register/names-register.component';
import { DatelessRegisterComponent } from './pages/dateless-register/dateless-register.component';
import { InitialsRegisterComponent } from './pages/initials-register/initials-register.component';
import { DatelessIndexChartComponent } from './pages/dateless-register/dateless-index-chart.component';
import { DatelessAuthoritiesComponent } from './pages/dateless-register/dateless-authorities.component';
import { ClassificationPillModule } from './components/classification-pill/classification-pill.module';
import { NameFinderComponent } from './pages/names-register/name-finder/name-finder.component';
import { NamesWatchlistComponent } from './pages/names-register/names-watchlist/names-watchlist.component';
import { NamesPopularityComponent } from './pages/names-register/names-popularity/names-popularity.component';
import { SafeHtmlPipe } from './pipes/SafeHtmlPipe';
import { SnowComponent } from './thirdpartycomponents/snow.component';
import { AdminBlogCreateComponent } from './pages/admin-blog-create/admin-blog-create.component';
import { AdminBlogConfigComponent } from './pages/admin-blog-config/admin-blog-config.component';
import { AdminStaticMediaComponent } from './pages/admin-static-media/admin-static-media.component';
import { CustomLoaderBoxComponent } from './components/custom-loader-box/custom-loader-box.component';
import { AdminPlateViewerToggleComponent } from './components/admin-plate-viewer-toggle/admin-plate-viewer-toggle.component';
import { DealerPortalComponent } from './pages/dealer-portal/dealer-portal.component';
import { RegistrationMetricsComponent } from './components/registration-metrics/registration-metrics.component';
import { RelativeTimePipe } from './pipes/RelativeTimePipe';
import { MetricPortalComponent } from './pages/metric-portal/metric-portal.component';
import { AdminRecentlyAddedComponent } from './pages/admin-recently-added/admin-recently-added.component';
import { AdminUsersComponent } from './pages/admin-users/admin-users.component';
import { AdminFounderCockpitComponent } from './pages/admin-founder-cockpit/admin-founder-cockpit.component';
import { AdminComparablesComponent } from './pages/admin-comparables/admin-comparables.component';
import { AdminValuationLabComponent } from './pages/admin-valuation-lab/admin-valuation-lab.component';
import { VlabResearchComponent } from './pages/admin-valuation-lab/research/vlab-research.component';
import { VlabUpcomingComponent } from './pages/admin-valuation-lab/upcoming/vlab-upcoming.component';
import { localDevAccess } from './dev-access';
import { accountV2Enabled, marketGuidesEnabled, showUnfinished, whatsOnYourPlateEnabled } from './site-config';
import { PUBLISHED_SERIES, seriesMatcher, seriesPath, seriesPeriodLabel, seriesSortKey } from './utils/series-url';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { LegacyRedirectComponent } from './pages/not-found/legacy-redirect.component';
import { AdminDealerHealthComponent } from './pages/admin-dealer-health/admin-dealer-health.component';
import {MetricGraphCollectionComponent} from "./components/metric-graph-collection/metric-graph-collection.component";
import { DealerMetricsChartComponent } from './components/dealer-metrics-chart/dealer-metrics-chart.component';
import {CountUpDirective} from "./pipes/count-up.directive";
import { BasicInputComponent } from './components/basic-input/basic-input.component';
import { NewReleaseSearchComponent } from './components/new-release-search/new-release-search.component';
import { LandingMarketWatchComponent } from './components/landing-market-watch/landing-market-watch.component';
import {FixedDigitPipe} from "./pipes/FixedDigitNumber";
import {AboutUsPageComponent} from "./pages/about-us-page/about-us-page.component";
import {AboutUsPageV2Component} from "./pages/about-us-page-v2/about-us-page-v2.component";
import {MetricCardComponent} from "./components/metric-card/metric-card.component";
import { PrivacyPolicyPageComponent } from './pages/privacy-policy-page/privacy-policy-page.component';
import { DataDeletionPageComponent } from './pages/data-deletion-page/data-deletion-page.component';
import { MarketWatchPageComponent } from './pages/market-watch-page/market-watch-page.component';
import { AdminSoldSearchComponent } from './pages/admin-sold-search/admin-sold-search.component';
import { AdminEditorialReviewComponent } from './pages/admin-editorial-review/admin-editorial-review.component';
import { AdminEditorialIntelligenceComponent } from './pages/admin-editorial-intelligence/admin-editorial-intelligence.component';
import { EditorialEnabledGuard } from './guards/editorial-enabled.guard';
import { AdminEditorialReviewQueueComponent } from './pages/admin-editorial-review-queue/admin-editorial-review-queue.component';
import { AdminEditorialWorkbenchComponent } from './pages/admin-editorial-workbench/admin-editorial-workbench.component';
import { AdminPlatePhotosComponent } from './pages/admin-plate-photos/admin-plate-photos.component';
import { EditorialImportPanelComponent } from './components/editorial-import-panel/editorial-import-panel.component';
import { AdminMultipleSalesComponent } from './pages/admin-multiple-sales/admin-multiple-sales.component';
import { AdminYearlyTopSalesComponent } from './pages/admin-yearly-top-sales/admin-yearly-top-sales.component';
import { SearchTrackingMetricsComponent } from './components/search-tracking-metrics/search-tracking-metrics.component';
import { TabStepperComponent } from './components/tab-stepper/tab-stepper.component';
import { SellModule } from './pages/sell/sell.module';
import { NewReleasePageV2Module } from './pages/new-release-page-v2/new-release-page-v2.module';
import { NewReleasePageV2Component } from './pages/new-release-page-v2/new-release-page-v2.component';
import { PlateDetailV2Module } from './pages/plate-detail-v2/plate-detail-v2.module';
import { PlateDetailV2Component } from './pages/plate-detail-v2/plate-detail-v2.component';
import { SellComponent } from './pages/sell/sell.component';
import { SellValuationPlaceholderComponent } from './pages/sell/components/sell-valuation-placeholder/sell-valuation-placeholder.component';

const defaultMetaDescription = HOME_DESCRIPTION;

// Since the 2026-11-03 release the redesigned Registry, search results and
// registration-series pages serve the canonical routes on every host.
const useRedesign = true;

// Unfinished DEV2 work (specialist redesigns, account v2, live auctions, draft
// routes): shown on dev2 and local dev builds only, never on staging or
// production. Decided per host in site-config.
const isDevOnly = showUnfinished;

// Release hosts (staging, production) use the compact launch site: retired
// entry points redirect and unknown routes get the not-found page.
const isRelease = !showUnfinished;

// /guides/{slug} page components, keyed by slug (see article-landing/guides.ts).
const GUIDE_COMPONENTS: { [slug: string]: any } = {
  'number-plate-types': NumberPlateTypesComponent,
  'private-number-plates': PrivateNumberPlatesComponent,
  'cherished-number-plates': CherishedNumberPlatesComponent,
  'dateless-number-plates': DatelessNumberPlatesComponent,
  'prefix-number-plates': PrefixNumberPlatesComponent,
  'suffix-number-plates': SuffixNumberPlatesComponent,
  'how-to-transfer-a-registration': HowToTransferARegistrationComponent,
  'how-to-retain-a-registration': HowToRetainARegistrationComponent,
  'how-to-save-money': HowToSaveMoneyComponent,
};

// /plates hub: only collections that are live on this host.
const PLATES_HUB_LINKS = [
  { label: 'Current style', route: '/plates/current-style', description: 'Every registration series since 2001.' },
  { label: 'Dateless plates', route: '/plates/dateless', description: 'The oldest, shortest registrations.' },
  { label: 'Initials', route: '/plates/initials', description: 'Match your initials.' },
  { label: 'Name plates', route: '/plates/name-plates', description: 'Plates that spell names.' },
  { label: 'Number 1 plates', route: '/plates/number-1', description: 'The ultimate number 1s.' },
  { label: 'Under £200', route: '/plates/budget/under-200', description: 'Great value registrations.' },
  ...(isDevOnly
    ? [
        { label: 'Prefix plates', route: '/plates/prefix', description: 'Letter-first 1983–2001 style.' },
        { label: 'Suffix plates', route: '/plates/suffix', description: 'Classic 1963–1983 style.' },
        { label: 'Budget plates', route: '/plates/budget', description: 'Great value registrations.' },
      ]
    : []),
];

// /plates/current-style: one link per published registration series, newest first.
const SERIES_HUB_LINKS = PUBLISHED_SERIES
  .slice()
  .sort((a, b) => seriesSortKey(b) - seriesSortKey(a))
  .map((nn) => ({
    label: `${nn} reg`,
    route: seriesPath(nn),
    description: `${seriesPeriodLabel(nn)} series`,
  }));

// Legacy specialist URLs on release hosts: 301 at the edge to their /plates/...
// homes (SEO spec redirect map), mirrored here for in-app links. dev2 keeps its
// review swaps on the old paths.
const RELEASE_LEGACY_REDIRECTS = isRelease
  ? [
      { path: 'initials-search', redirectTo: 'plates/initials', pathMatch: 'full' },
      { path: 'initials-search/:initials', redirectTo: 'plates/initials/:initials', pathMatch: 'full' },
      { path: 'under-200', redirectTo: 'plates/budget/under-200', pathMatch: 'full' },
      { path: 'names', redirectTo: 'plates/name-plates', pathMatch: 'full' },
      { path: 'number-1', redirectTo: 'plates/number-1', pathMatch: 'full' },
      { path: 'dateless-search', redirectTo: 'plates/dateless', pathMatch: 'full' },
    ]
  : [];

const routes: Routes = [
  // LOCAL DEV ACCESS (localhost only, never deployed sites): admin dev tools without a login.
  ...(localDevAccess
    ? [{ path: 'dev/valuation-lab', component: AdminValuationLabComponent,
         data: { title: 'PlateX - Valuation Lab (local dev)', index: false } }]
    : []),
  // MAIN
  {
    // Homepage: dev2/local get the v2 shell (Homepage A public / Homepage B
    // "My Market" member, one route, composition switched on auth state —
    // docs/my-market/). Production keeps the legacy landing until release.
    path: '', component: HomeV2Component, data: {
      title: PAGE_META['/'].title,
      description: PAGE_META['/'].description,
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    }
  },
  ...RELEASE_LEGACY_REDIRECTS,
  // Editorial content: /guides/{slug} and /blog/{slug} are canonical (SEO
  // spec); /articles/... and /blogs/... 301 here.
  { path: 'articles', redirectTo: 'guides', pathMatch: 'full' },
  { path: 'articles/:slug', redirectTo: 'guides/:slug', pathMatch: 'full' },
  { path: 'blogs', redirectTo: 'blog', pathMatch: 'full' },
  { path: 'blogs/:blogHandle', redirectTo: 'blog/:blogHandle', pathMatch: 'full' },
  // PlateX Market Guides (Insights & Opinion): library, guide landing pages
  // and the entitled reader. /guides stays the SEO how-to series. Off on www
  // until approved (site-config marketGuides); the landing becomes indexable
  // on production only, like every other page.
  ...(marketGuidesEnabled
    ? [
        { path: 'market-guides', component: GuidesLibraryComponent, data: {
            title: 'Market Guides | PlateX Insights',
            description: 'PlateX Market Guides: original research into the UK registration market, published as standalone guides.',
            index: true,
          } },
        { path: 'market-guides/:slug', component: GuideLandingComponent, data: {
            title: 'PlateX Market Guides',
            description: 'Original PlateX research into the UK registration market.',
            index: true,
          } },
        { path: 'market-guides/:slug/read', component: GuideReaderComponent, data: { title: 'PlateX Market Guides', index: false } },
      ]
    : []),
  // What's on Your Plate: on www from launch (owner, 4 November 2026).
  ...(whatsOnYourPlateEnabled
    ? [{ path: 'whats-on-your-plate', component: WhatsOnYourPlateComponent, data: {
        title: "What's on Your Plate | PlateX",
        description: 'PlateX supports Samaritans, Mind and ANDYSMANCLUB. What is on your plate? Ask a mate.',
        index: true,
      } }]
    : []),
  // The legacy landing page is retired on release hosts: its entry points
  // (/landing, /buy, /homepage) 301 to / at the edge and redirect in-app.
  ...(isRelease
    ? [
        { path: 'landing', redirectTo: '', pathMatch: 'full' },
        { path: 'landing/:type', redirectTo: '', pathMatch: 'full' },
        { path: 'buy', redirectTo: '', pathMatch: 'full' },
        { path: 'homepage', redirectTo: '', pathMatch: 'full' },
      ]
    : []),
  {
    path: 'landing', component: LandingComponent, data: {
      description: defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    }
  },
  {
    path: 'landing/:type', component: LandingComponent, data: {
      description: defaultMetaDescription,
      index: true
    }
  },
  {
    path: 'buy', component: LandingComponent, data: {
      description: defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    }
  },
  // /sell-draft: previous draft of the sell landing (LandingComponent dual-mode).
  // Retained for reference; not linked from anywhere in the app.
  ...(isDevOnly ? [{ path: 'sell-draft', component: LandingComponent }] : []),
  {
    path: 'sell',
    component: SellComponent,
    data: {
      description: 'Get an instant valuation, understand likely demand, and place your plate in front of the right buyers.',
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true,
    }
  },
  {
    path: 'sell/valuation/:registration',
    component: SellValuationPlaceholderComponent,
    data: {
      title: 'Generating your valuation - PlateX',
      description: 'Your PlateX valuation is being prepared.',
      index: false,
    }
  },

  { path: '_buy', component: BuyPageComponent },
  { path: '_sell', component: SellingLandingPageComponent },

  // About page: DEV environments (dev2 host + local dev) serve the redesigned
  // V2 page; real production continues to serve the existing AboutUsPageComponent.
  // The production /about-us experience is therefore untouched.
  // /about is the canonical About address (SEO spec); /about-us 301s to it.
  // The redesigned V2 page stays dev-only until approved.
  { path: 'about', component: isDevOnly ? AboutUsPageV2Component : AboutUsPageComponent, data: {
      title: PAGE_META['/about'].title,
      description: PAGE_META['/about'].description,
      index: true,
    } },
  { path: 'about-us', redirectTo: 'about', pathMatch: 'full' },
  { path: 'privacy-policy', component: PrivacyPolicyPageComponent, data: { ...PAGE_META['/privacy-policy'], index: true } },
  { path: 'data-deletion', component: DataDeletionPageComponent, data: { ...PAGE_META['/data-deletion'], index: false } },

  // { path: 'auctions', component: AuctionsComponent },
  // Live Auctions (dev2/local only): every live number plate auction lot in one
  // place. Production keeps no /auctions route (falls through to the wildcard).
  ...(isDevOnly
    ? [
        {
          path: 'auctions',
          component: LiveAuctionsComponent,
          data: {
            title: 'Live Number Plate Auctions | PlateX',
            description: 'Every live number plate auction in one place, with all-in prices after fees.',
            index: false,
          },
        },
      ]
    : []),
  { path: 'message-hub', component: MessengerPageComponent },
  { path: 'info', component: InfoComponent },
  {
    path: 'plate-viewer',
    component: PlateViewerPageComponent,
    data: {
      title: PAGE_META['/plate-viewer'].title,
      description: PAGE_META['/plate-viewer'].description,
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    },
  },
  {
    path: 'plate-viewer/:type',
    component: PlateViewerPageComponent,
    data: {
      title: 'Plate Viewer - see plate on car',
      description: 'View Number Plates on Car - Photorealistic number plate viewer.  See any number plate on a huge choice of cars from Fiat to Ferrari then download your image and share!',
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    },
  },
  {
    path: 'plate-viewer/:type/:input',
    component: PlateViewerPageComponent,
    data: {
      title: 'Plate Viewer - see plate on car',
      description: 'View Number Plates on Car - Photorealistic number plate viewer.  See any number plate on a huge choice of cars from Fiat to Ferrari then download your image and share!',
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    },
  },
  {
    path: 'plate-viewer/:type/:input/:plate',
    component: PlateViewerPageComponent,
    data: {
      title: 'Plate Viewer - see plate on car',
      description: 'View Number Plates on Car - Photorealistic number plate viewer.  See any number plate on a huge choice of cars from Fiat to Ferrari then download your image and share!',
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true
    },
  },
  { path: 'info/glossary', component: InfoComponent },
  { path: 'info/glossary/:term', component: InfoComponent },
  // {
  //   path: 'compare', component: ComparePageComponent,
  //   data: {
  //     title: 'PlateX - Price Compare',
  //     description: 'FREE and instant price comparison across 40+ UK private number plate dealers. Find the best deal on over 80 million personal plates with the UK’s private plate portal.',
  //     image: 'https://cdn.platex.co.uk/banners/HATE_TAX_DIABLO_DT.png',
  //     index: true
  //   },
  // },
  {
    path: 'compare/:registration',
    component: ComparePageComponent,
    data: {
      title: 'PlateX - {registration} Compare Results',
      description: 'FREE and instant price comparison across 40+ UK private number plate dealers. Find the best deal on over 80 million personal plates with the UK’s private plate portal.',
      image: 'https://cdn.platex.co.uk/banners/HATE_TAX_DIABLO_DT.png',
      index: false
    },
  },
  { path: 'create-auction', component: CreateAuctionComponent },
  {
    path: 'byo-search', component: BYOSearchPageComponent,
    data: {
      title: PAGE_META['/byo-search'].title,
      description: PAGE_META['/byo-search'].description,
      image: 'https://cdn.platex.co.uk/large/DE51GNS_RANGEROVER_DEFENDER.png'
    },
  },
  {
    path: 'byo-search/:style',
    component: BYOSearchPageComponent,
    data: {
      title: 'PlateX - Build Your Own',
      description: 'Create your own private number plate easily using our Make Your Own personal number plate tool and choose from 80 million registrations.',
      image: 'https://cdn.platex.co.uk/large/DE51GNS_RANGEROVER_DEFENDER.png',
      index: false
    },
  },
  {
    path: 'byo-search/:style/:prefix/:numbers/:letters',
    component: BYOSearchPageComponent,
    data: {
      title: 'PlateX - Build Your Own',
      description: 'Create your own private number plate easily using our Make Your Own personal number plate tool and choose from 80 million registrations.',
      image: 'https://cdn.platex.co.uk/large/DE51GNS_RANGEROVER_DEFENDER.png',
      index: false
    },
  },
  // CANONICAL plate route. Individual plates now live at /plate/{REG}; this is
  // the path the self-referencing canonical + og:url + crawlable search anchors
  // all point at (see canonical-url.ts / route-data-service). Same component as
  // the legacy /plate-detail route below (dev2 → V2, prod/localhost → legacy
  // page), reading the same :registration/:term params, so the experience is
  // identical regardless of which path the user arrives on.
  // Registry: the canonical address is /plates/{REGISTRATION} (declared after
  // the /plates taxonomy routes below so hub slugs win). /plate/... and
  // /plate-detail/... are legacy aliases: the CDN edge 301s them (keeping
  // pa/pas), and these in-app redirects keep query params for in-app links.
  { path: 'plate/:registration', redirectTo: 'plates/:registration' },
  { path: 'plate/:registration/:term', redirectTo: 'plates/:registration' },
  // LEGACY plate-detail alias — kept rendering (not a hard redirect) so existing
  // inbound links, bookmarks and the many internal /plate-detail/... navigations
  // keep working and preserve their query params (e.g. ?t=checkout). It no longer
  // owns the canonical: route-data-service canonicalises /plate-detail/{REG} to
  // /plate/{REG}, so search engines collapse the two onto the canonical path.
  // dev2 → Plate Detail V2; prod/localhost → legacy page. The standalone legacy
  // page stays reachable at /plate-detail-legacy/... below for comparison.
  { path: 'plate-detail/:registration', redirectTo: 'plates/:registration' },
  { path: 'plate-detail/:registration/:term', redirectTo: 'plates/:registration' },
  // Side-by-side comparison paths (dev only).
  ...(isDevOnly
    ? [
      // Legacy plate detail — always the original page (never swapped), so it stays
      // available for side-by-side comparison on dev2 and as a stable fallback.
      {
        path: 'plate-detail-legacy/:registration',
        component: PlateDetailPageComponent,
        data: {
          title: 'PlateX - {registration} Detail',
          index: false
        },
      },
      {
        path: 'plate-detail-legacy/:registration/:term',
        component: PlateDetailPageComponent,
        data: {
          title: 'PlateX - {registration} Detail',
          index: false
        },
      },
      // dev2 redesign — shared plate detail shell (New Issue + Resale), parked on
      // a separate path while it is built out stage by stage.
      {
        path: 'plate-detail-v2/:registration',
        component: PlateDetailV2Component,
        data: {
          title: 'PlateX - {registration} Detail',
          index: false
        },
      },
      {
        path: 'plate-detail-v2/:registration/:term',
        component: PlateDetailV2Component,
        data: {
          title: 'PlateX - {registration} Detail',
          index: false
        },
      },
      ]
    : []),
  {
    // Admin-only Editorial Intelligence workspace (self-verifies admin in component).
    // Default registration (FU2) resolves in the component when no param is given.
    path: 'admin/editorial-intelligence',
    component: AdminEditorialIntelligenceComponent,
    data: {
      title: 'Editorial Intelligence',
      index: false
    },
  },
  {
    path: 'admin/editorial-intelligence/:registration',
    component: AdminEditorialIntelligenceComponent,
    data: {
      title: 'Editorial Intelligence - {registration}',
      index: false
    },
  },
  {
    path: 'search-results/:searchCriteria',
    component: SearchResultsPageComponent,
    data: {
      title: 'PlateX - Compare and save on {searchCriteria} number plates',
      description: 'Find all {searchCriteria} private number plates. ' + defaultMetaDescription,
      index: false
    },
  },
  // DEV-only comparison route: renders the production search-results UX with the
  // DEV2 (live_v2) search data. Reuses SearchResultsPageComponent; prodStyleCompare
  // forces the legacy body even on dev2. Both /search-prod-style?q=TERM and
  // /search-prod-style/TERM are accepted. Gated to the dev2 host so it can never
  // become a public production route.
  ...(isDevOnly
    ? [
        {
          path: 'search-prod-style/:searchCriteria',
          component: SearchResultsPageComponent,
          data: { title: 'PlateX DEV - production-style search', index: false, prodStyleCompare: true },
        },
        {
          path: 'search-prod-style',
          component: SearchResultsPageComponent,
          data: { title: 'PlateX DEV - production-style search', index: false, prodStyleCompare: true },
        },
      ]
    : []),
  // ───────────────────────────────────────────────────────────────────────
  // SEO Phase 2B — /plates taxonomy shells (dev2-first). Data-driven clean
  // landing pages backed by the single TaxonomyShellComponent; real plate
  // listings are wired in a later phase. Each is the canonical destination
  // that the legacy aliases below (dateless-search, initials-search, under-200,
  // names, number-1, new-release) point their canonical at via data.canonical.
  // ───────────────────────────────────────────────────────────────────────
  {
    path: 'plates',
    component: TaxonomyShellComponent,
    data: {
      title: PAGE_META['/plates'].title,
      description: PAGE_META['/plates'].description,
      image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
      index: true,
      shell: {
        heading: 'Private Number Plates',
        intro: [
          'Find your perfect private number plate. Browse by style — dateless, prefix, suffix and current-style — or jump straight to popular collections like initials, budget plates, names and number 1s.',
          'Every plate is price-compared across 40+ UK dealers, the DVLA and private sellers.',
        ],
        linksHeading: 'Browse by type',
        links: PLATES_HUB_LINKS,
        showViewerCta: true,
        showValuationCta: true,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Plates' },
        ],
      },
    },
  },
  {
    // Phase 2C: re-homed onto the real dateless search page (was a holding
    // shell). Self-canonicalises via its own path — no data.canonical override.
    // Phase 8 dev2 route swap (build pack §3.1): dev2 serves the approved
    // Dateless Reference; prod keeps the search page until the prod release.
    // Reversible: flip the ternary back to restore the old page everywhere.
    path: 'plates/dateless',
    component: DatelessRegisterComponent,
    data: {
      title: PAGE_META['/plates/dateless'].title,
      description: 'Britain\'s original registrations, issued 1903 to 1963. Explore the history, the market, format values and the live register of dateless plates.',
      image: 'https://cdn.platex.co.uk/large/8UYS_FERRARI_275.png',
      index: true,
    },
  },
  {
    // Format deep link (§4.2): page state, not an indexable facet. Canonical
    // collapses to /plates/dateless; noindex. Introduced at Phase 8 with the
    // swap; on prod the fallback search page renders until release.
    path: 'plates/dateless/:format',
    component: DatelessRegisterComponent,
    data: {
      title: 'Dateless Number Plates - The PlateX Reference',
      description: 'Britain\'s original registrations, issued 1903 to 1963. Explore the history, the market, format values and the live register of dateless plates.',
      index: false,
      canonical: '/plates/dateless',
    },
  },
  ...(isDevOnly
    ? [
      {
        path: 'plates/prefix',
        component: TaxonomyShellComponent,
        data: {
          title: 'Prefix Number Plates | PlateX',
          description: 'Browse prefix-style private number plates (1983–2001), where a leading letter denotes the year. ' + defaultMetaDescription,
          image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
          index: true,
          shell: {
            heading: 'Prefix Number Plates',
            intro: [
              'Prefix plates run a single leading letter followed by numbers and three letters (for example A1 BCD). Issued between 1983 and 2001, they are a popular way to find initials and short combinations.',
            ],
            linksHeading: 'Related',
            links: [
              { label: 'All plate types', route: '/plates' },
              { label: 'Suffix plates', route: '/plates/suffix' },
              { label: 'Current style', route: '/plates/current-style' },
              { label: 'Initials', route: '/plates/initials' },
            ],
            showViewerCta: true,
            showValuationCta: true,
            breadcrumbs: [
              { label: 'Home', route: '/' },
              { label: 'Plates', route: '/plates' },
              { label: 'Prefix' },
            ],
          },
        },
      },
      ]
    : []),
  ...(isDevOnly
    ? [
      {
        path: 'plates/suffix',
        component: TaxonomyShellComponent,
        data: {
          title: 'Suffix Number Plates | PlateX',
          description: 'Browse suffix-style private number plates (1963–1983), where a trailing letter denotes the year. ' + defaultMetaDescription,
          image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
          index: true,
          shell: {
            heading: 'Suffix Number Plates',
            intro: [
              'Suffix plates carry three letters, up to three numbers and a trailing year letter (for example BCD 1A). Issued between 1963 and 1983, they offer classic-look combinations at accessible prices.',
            ],
            linksHeading: 'Related',
            links: [
              { label: 'All plate types', route: '/plates' },
              { label: 'Prefix plates', route: '/plates/prefix' },
              { label: 'Dateless plates', route: '/plates/dateless' },
              { label: 'Budget plates', route: '/plates/budget' },
            ],
            showViewerCta: true,
            showValuationCta: true,
            breadcrumbs: [
              { label: 'Home', route: '/' },
              { label: 'Plates', route: '/plates' },
              { label: 'Suffix' },
            ],
          },
        },
      },
      ]
    : []),
  {
    path: 'plates/current-style',
    component: TaxonomyShellComponent,
    data: {
      title: PAGE_META['/plates/current-style'].title,
      description: PAGE_META['/plates/current-style'].description,
      image: 'https://cdn.platex.co.uk/pages/new-release/BERSERK_AMG.png',
      index: true,
      shell: {
        heading: 'Current Style Number Plates',
        intro: [
          'Current-style plates use two letters, two numbers and three letters (for example AB12 CDE). Introduced in September 2001, they carry a two-digit age identifier set by the DVLA: a new series is released every March and September.',
          'Choose a registration series below to see its best plates, prices and sales history.',
        ],
        linksHeading: 'Registration series',
        links: SERIES_HUB_LINKS,
        showViewerCta: true,
        showValuationCta: true,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Plates', route: '/plates' },
          { label: 'Current style' },
        ],
      },
    },
  },
  {
    // Phase 2C: re-homed onto the real initials search page (was a holding
    // shell). Self-canonicalises via its own path — no data.canonical override.
    path: 'plates/initials',
    component: InitialsRegisterComponent,
    data: {
      title: PAGE_META['/plates/initials'].title,
      description: PAGE_META['/plates/initials'].description,
      image: 'https://cdn.platex.co.uk/large/L5_LAMBORGHINI_DIABLO.png',
      index: true,
    },
  },
  {
    // Phase 2C: per-initials results now served by the real initials search
    // page (e.g. /plates/initials/bf). Self-canonicalises via its own path; the
    // {initials} token in title/description is filled by route-data-service.
    path: 'plates/initials/:initials',
    component: InitialsRegisterComponent,
    data: {
      title: PAGE_META['/plates/initials/{initials}'].title,
      description: PAGE_META['/plates/initials/{initials}'].description,
      image: 'https://cdn.platex.co.uk/large/L5_LAMBORGHINI_DIABLO.png',
      index: true,
    },
  },
  ...(isDevOnly
    ? [
      {
        path: 'plates/budget',
        component: TaxonomyShellComponent,
        data: {
          title: 'Budget Number Plates | PlateX',
          description: "Cheap private plates. We've gathered great-value private number plates from across the market in one place. " + defaultMetaDescription,
          image: 'https://cdn.platex.co.uk/banners/SUB_200_FIAT_500_DT.png',
          index: true,
          shell: {
            heading: 'Budget Number Plates',
            intro: [
              'A private plate does not have to cost a fortune. Browse budget registrations by price bracket, all price-compared across the whole market.',
            ],
            linksHeading: 'Browse by budget',
            links: [
              { label: 'Under £200', route: '/plates/budget/under-200', description: 'Our cheapest plates.' },
              { label: 'All plate types', route: '/plates' },
              { label: 'Suffix plates', route: '/plates/suffix' },
            ],
            showViewerCta: true,
            showValuationCta: true,
            breadcrumbs: [
              { label: 'Home', route: '/' },
              { label: 'Plates', route: '/plates' },
              { label: 'Budget' },
            ],
          },
        },
      },
      ]
    : []),
  {
    // Phase 2C: re-homed onto the real under-£200 listing page (was a holding
    // shell). Literal path declared before the generic :bracket shell so it
    // takes precedence. Self-canonicalises via its own path.
    path: 'plates/budget/under-200',
    // Redesigned Register-style page on every host (owner, 2026-11-03).
    component: Under200RegisterComponent,
    data: {
      title: PAGE_META['/plates/budget/under-200'].title,
      description: PAGE_META['/plates/budget/under-200'].description,
      image: 'https://cdn.platex.co.uk/banners/SUB_200_FIAT_500_DT.png',
      index: true,
    },
  },
  ...(isDevOnly
    ? [
      {
        // Per-bracket budget shell (e.g. other future price bands). Self-canonical
        // via its own path; brackets are added as live routes rather than
        // pre-generating every price band. under-200 is handled above by the real
        // listing page.
        path: 'plates/budget/:bracket',
        component: TaxonomyShellComponent,
        data: {
          title: 'Cheap Number Plates Under £200 | PlateX',
          description: "Cheap private plates. We've gathered all private number plates under £200 from across the market in one place. " + defaultMetaDescription,
          image: 'https://cdn.platex.co.uk/banners/SUB_200_FIAT_500_DT.png',
          index: true,
          shell: {
            heading: 'Number Plates Under £200',
            intro: [
              'Our cheapest private number plates, all under £200 and price-compared across 40+ UK dealers, the DVLA and private sellers.',
            ],
            linksHeading: 'Related',
            links: [
              { label: 'All budget plates', route: '/plates/budget' },
              { label: 'All plate types', route: '/plates' },
            ],
            showViewerCta: true,
            showValuationCta: true,
            breadcrumbs: [
              { label: 'Home', route: '/' },
              { label: 'Plates', route: '/plates' },
              { label: 'Budget', route: '/plates/budget' },
              { label: 'Under £200' },
            ],
          },
        },
      },
      ]
    : []),
  {
    // Phase 2C: re-homed onto the real name-plate page (was a holding shell).
    // Self-canonicalises via its own path — no data.canonical override.
    path: 'plates/name-plates',
    component: NamesRegisterComponent,
    data: {
      title: PAGE_META['/plates/name-plates'].title,
      description: PAGE_META['/plates/name-plates'].description,
      image: 'https://cdn.platex.co.uk/pages/name-search/JAMES_RR.png',
      index: true,
    },
  },
  { path: 'plates/names', redirectTo: 'plates/name-plates', pathMatch: 'full' },
  {
    // Phase 2C: re-homed onto the real number-1 page (was a holding shell).
    // Self-canonicalises via its own path — no data.canonical override.
    // On dev2 the canonical route serves the approved "Number Ones · The
    // Register" redesign for review; production keeps the original page.
    // Same isDev2 hostname swap used by plate-detail / new-release.
    path: 'plates/number-1',
    component: NumberOnesRegisterComponent,
    data: {
      title: PAGE_META['/plates/number-1'].title,
      description: PAGE_META['/plates/number-1'].description,
      image: 'https://cdn.platex.co.uk/banners/NUM_1_DBS_DT.png',
      index: true,
    },
  },
  // Registration-series pages: /plates/{NN}-reg (published series only).
  {
    matcher: seriesMatcher,
    component: NewReleasePageV2Component,
    data: {
      series: true,
      image: 'https://cdn.platex.co.uk/pages/new-release/BERSERK_AMG.png',
      index: true,
    },
  },
  // Registry: /plates/{REGISTRATION}. Declared after every /plates/... hub so
  // a hub slug always wins; the edge returns 404 for non-registration slugs.
  {
    path: 'plates/:registration',
    component: PlateDetailV2Component,
    data: {
      title: '{registration} Number Plate | PlateX',
      index: true
    },
  },
  ...(isDevOnly
    ? [
      {
        path: 'valuation',
        component: TaxonomyShellComponent,
        data: {
          title: 'Private Number Plate Valuation | PlateX',
          description: 'Get a free valuation for your private number plate, backed by real market data from across 40+ UK dealers, the DVLA and private sellers.',
          image: 'https://cdn.platex.co.uk/banners/PLATEX_R8.jpg',
          index: true,
          shell: {
            heading: 'Value My Number Plate',
            intro: [
              'Wondering what your private plate is worth? Our valuation draws on real market data from across the whole UK market to give you a clear, current estimate.',
              'Plate valuations are coming soon — in the meantime, explore the market to see comparable plates and prices.',
            ],
            linksHeading: 'Explore the market',
            links: [
              { label: 'Browse all plates', route: '/plates' },
              { label: 'Dateless plates', route: '/plates/dateless' },
              { label: 'Number 1 plates', route: '/plates/number-1' },
            ],
            showViewerCta: true,
            showValuationCta: false,
            breadcrumbs: [
              { label: 'Home', route: '/' },
              { label: 'Valuation' },
            ],
          },
        },
      },
      ]
    : []),

  // initials
  {
    path: 'initials-search',
    component: InitialsSearchPageComponent,
    data: {
      title: 'PlateX - Initials Search',
      description: 'Find your ultimate initials private number plate. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/L5_LAMBORGHINI_DIABLO.png',
      index: true,
      // Legacy alias: renders here, canonical points at the new taxonomy URL.
      canonical: '/plates/initials'
    },
  },
  {
    path: 'initials-search/:initials',
    component: InitialsSearchPageComponent,
    data: {
      title: 'PlateX - {initials} Initials Results',
      description: 'Find your ultimate initials private number plate. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/L5_LAMBORGHINI_DIABLO.png',
      index: false,
      // Legacy alias: canonical points at the new per-initials taxonomy URL.
      canonical: '/plates/initials/{initials}'
    },
  },
  // Legacy series address: /new-release/{NN} -> /plates/{NN}-reg (301 at the edge).
  {
    path: 'new-release/:releaseYear',
    component: LegacyRedirectComponent,
    data: { legacyTarget: 'series', index: false },
  },
  ...(isDevOnly
    ? [
      {
        path: 'new-release-draft/:releaseYear',
        component: NewReleasePageV2Component,
        data: {
          title: 'PlateX - {releaseYear} Release (draft redesign)',
          description: 'Find the best words and names from the new style registrations released with our powerful search tool. ' + defaultMetaDescription,
          image: 'https://cdn.platex.co.uk/pages/new-release/BERSERK_AMG.png',
          index: false
        },
      },
      ]
    : []),
  // Names
  // DEV2 REVIEW: /names serves the new Register so it can be reviewed in its
  // real home. This branch is dev2-only and must NOT reach master until the
  // replacement of the public page is a decision in its own right — merging it
  // as-is would swap the live /names for the redesign.
  {
    path: 'names',
    component: NamesRegisterComponent,
    data: {
      title: 'PlateX - Names · The Perfect Register',
      description:
        'Every British registration that spells a name exactly. ' + defaultMetaDescription,
      iamge: 'https://cdn.platex.co.uk/pages/name-search/JAMES_RR.png',
      // Non-indexed while this is a review swap. The old page's `index: true`
      // and its `canonical: '/plates/names'` both have to go with the
      // component: /plates/names still serves NamePlatePageComponent, so
      // canonicalising there would point the redesign at a different page.
      index: false
    },
  },
  // categories
  {
    path: 'categories/:tag',
    component: MultiSearchPageComponent,
    data: { index: false },
  },
  {
    path: 'categories/:tag/:searchCriteria',
    component: MultiSearchPageComponent,
    data: { index: false },
  },
  // sub £200
  {
    path: 'under-200',
    component: UnderOneHundredComponent,
    data: {
      title: 'PlateX - Under 200',
      description: "Cheap Private Plates. We've gathered all private number plates under £200 from across the market in one place only on the UKs private number plate portal.",
      image: 'https://cdn.platex.co.uk/banners/SUB_200_FIAT_500_DT.png',
      index: true,
      // Legacy alias: canonical points at the new budget taxonomy URL.
      canonical: '/plates/budget/under-200'
    }
  },
  // dateless draft — Phase 1 static shell of the approved Dateless Reference
  // (build pack v4, prototype v13). UNLINKED review route (no nav links,
  // noindex); the canonical /plates/dateless swap happens only at Phase 8
  // with approval. Same pattern as names-draft / number-1-draft.
  {
    path: 'dateless-draft',
    component: DatelessRegisterComponent,
    data: {
      title: 'PlateX - Dateless · The PlateX Reference (draft redesign)',
      description: 'Britain\'s original registrations, issued 1903 to 1963. ' + defaultMetaDescription,
      index: false
    }
  },
  // dateless draft deep link — Phase 2-7 development/testing target for the
  // format token (letters × numbers, e.g. 2x1). The public
  // plates/dateless/:format route is introduced only at Phase 8.
  {
    path: 'dateless-draft/:format',
    component: DatelessRegisterComponent,
    data: {
      title: 'PlateX - Dateless · The PlateX Reference (draft redesign)',
      description: 'Britain\'s original registrations, issued 1903 to 1963. ' + defaultMetaDescription,
      index: false
    }
  },
  // initials draft — Gate 1 static shell of the approved Initials Reference
  // (build pack v5 prototype). UNLINKED review route (no nav links, noindex);
  // the canonical /plates/initials swap happens only at the final gate with
  // approval. Same pattern as dateless-draft.
  {
    path: 'initials-draft',
    component: InitialsRegisterComponent,
    data: {
      title: 'PlateX - Initials · The PlateX Initials Reference (draft redesign)',
      description: 'Your initials on every format of UK registration. ' + defaultMetaDescription,
      index: false
    }
  },
  // initials draft deep link — Gate 2+ development/testing target for the
  // initials token (1 to 3 letters, e.g. hs). The public
  // plates/initials/:initials swap is introduced only at the final gate.
  {
    path: 'initials-draft/:initials',
    component: InitialsRegisterComponent,
    data: {
      title: 'PlateX - Initials · The PlateX Initials Reference (draft redesign)',
      description: 'Your initials on every format of UK registration. ' + defaultMetaDescription,
      index: false
    }
  },
  // names draft — Task 3 static shell of the approved Names · The Perfect
  // Register redesign. UNLINKED review route (no nav links, noindex); the
  // canonical /plates/names swap happens after Task 4. Same pattern as
  // number-1-draft.
  {
    path: 'names-draft',
    component: NamesRegisterComponent,
    data: {
      title: 'PlateX - Names · The Perfect Register (draft redesign)',
      description: 'A collection of the best name private plates. ' + defaultMetaDescription,
      index: false
    }
  },
  // number 1 draft — always serves the approved redesign (review fallback on
  // any host + local dev verification). Same pattern as new-release-draft.
  {
    path: 'number-1-draft',
    component: NumberOnesRegisterComponent,
    data: {
      title: 'PlateX - Number Ones · The Register (draft redesign)',
      description: 'All the Number 1 registrations. ' + defaultMetaDescription,
      index: false
    }
  },
  // number 1 (dev2 serves the approved redesign — see plates/number-1 above)
  {
    path: 'number-1',
    component: isDevOnly ? NumberOnesRegisterComponent : NumberOnePageComponent,
    data: {
      title: 'PlateX - Number 1 Plates',
      description: 'All the Number 1 registrations. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/banners/NUM_1_DBS_DT.png',
      // Legacy alias: canonical points at the new taxonomy URL.
      canonical: '/plates/number-1'
    }
  },
  // dateless by size
  {
    path: 'dateless-search',
    component: DatelessSearchPageComponent,
    data: {
      title: 'PlateX - Dateless Search',
      description: 'The largest collection of dateless private plates. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/8UYS_FERRARI_275.png',
      // Legacy alias: canonical points at the new taxonomy URL.
      canonical: '/plates/dateless'
    },
  },
  {
    path: 'dateless-search/:length',
    component: DatelessSearchPageComponent,
    data: {
      title: 'PlateX - Dateless Search',
      description: 'The largest collection of dateless private plates. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/8UYS_FERRARI_275.png'
    },
  },
  {
    path: 'dateless-search/:length/:searchCriteria',
    component: DatelessSearchPageComponent,
    data: {
      title: 'PlateX - {searchCriteria} Dateless Results',
      description: 'The largest collection of dateless private plates. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/8UYS_FERRARI_275.png'
    },
  },
  {
    path: 'dateless-search/:length/:searchCriteria/:startingWith',
    component: DatelessSearchPageComponent,
    data: {
      title: 'PlateX - {searchCriteria} Dateless Results',
      description: 'The largest collection of dateless private plates. ' + defaultMetaDescription,
      image: 'https://cdn.platex.co.uk/large/8UYS_FERRARI_275.png'
    },
  },

  // dealer
  {
    path: 'dealer/home',
    component: DealerPortalComponent,
    data: {
      title: 'PlateX - Dealer Portal',
      index: false
    },
  },

  // dvla auction
  // {
  //   path: 'dvla-auction',
  //   component: DvlaAuctionPageComponent,
  // },
  // {
  //   path: 'dvla-auction/:searchCriteria',
  //   component: DvlaAuctionPageComponent,
  // },
  // account
  { path: 'login', component: LoginPageComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'forgot-password', component: ForgotPasswordPageComponent },
  { path: 'reset-password', component: ResetPasswordPageComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'data-deletion', component: DataDeletionComponent },
  // {
  //   path: 'order-confirmation/:orderId',
  //   component: OrderConfirmationComponent,
  // },
  // { path: 'checkout-cancel', component: CheckoutCancelComponent },
  // EDITORIAL (Dev2-only; all routes guarded by EditorialEnabledGuard — redirect
  // to /landing when environment.editorialEnabled is false, i.e. real prod).
  {
    // Admin-only Editorial Intelligence workspace (self-verifies admin in component).
    // Default registration (FU2) resolves in the component when no param is given.
    path: 'admin/editorial-intelligence',
    component: AdminEditorialIntelligenceComponent,
    canActivate: [EditorialEnabledGuard],
    data: {
      title: 'Editorial Intelligence',
      index: false
    },
  },
  {
    // Dev2-only editorial article review workflow (queue + review). Flag-gated.
    path: 'admin/editorial-review',
    component: AdminEditorialReviewQueueComponent,
    canActivate: [EditorialEnabledGuard],
    data: {
      title: 'Editorial Article Review',
      index: false
    },
  },
  {
    path: 'admin/plate-photos',
    component: AdminPlatePhotosComponent,
    data: {
      title: 'Plate Photos Review',
      index: false
    },
  },
  {
    path: 'admin/editorial-intelligence/:registration',
    component: AdminEditorialIntelligenceComponent,
    canActivate: [EditorialEnabledGuard],
    data: {
      title: 'Editorial Intelligence - {registration}',
      index: false
    },
  },
  {
    // Pass A3: read-only Editorial Workbench (inspects persisted artifacts).
    path: 'admin/editorial-workbench',
    component: AdminEditorialWorkbenchComponent,
    canActivate: [EditorialEnabledGuard],
    data: { title: 'Editorial Workbench', index: false },
  },
  {
    path: 'admin/editorial-workbench/:registration',
    component: AdminEditorialWorkbenchComponent,
    canActivate: [EditorialEnabledGuard],
    data: { title: 'Editorial Workbench - {registration}', index: false },
  },
  // ACCOUNT
  // Account v2 (the market-monitoring workspace) on every host since the
  // 2026-11-03 release; site-config's accountV2 switch can bring back the
  // legacy account page on a host whose API lacks /api/account.
  { path: 'account', component: accountV2Enabled ? AccountV2Component : AccountComponent, data: {
      title: 'PlateX - Account',
      index: false,
      pane: 'overview'
    }
  },
  ...(accountV2Enabled
    ? [
        { path: 'account/searches', component: AccountV2Component, data: { title: 'PlateX - Saved searches', index: false, pane: 'searches' } },
        // Saved-search builder + results (SAVED_SEARCH_ALERTS_PLAN steps 4/4a/5).
        // "See all" in the digest email links to saved-search/:id.
        { path: 'account/searches/new', component: SavedSearchBuilderComponent, data: { title: 'PlateX - New saved search', index: false } },
        { path: 'account/searches/:id/edit', component: SavedSearchBuilderComponent, data: { title: 'PlateX - Edit saved search', index: false } },
        { path: 'saved-search/:id', component: SavedSearchResultsComponent, data: { title: 'PlateX - Saved search', index: false } },
        { path: 'saved', component: SavedGuestComponent, data: { title: 'PlateX - Saved plates', index: false } },
        { path: 'account/saved', component: AccountV2Component, data: { title: 'PlateX - Saved plates', index: false, pane: 'saved' } },
        // The legacy favourites page is replaced by Saved plates in the new
        // account (same data), so old links never land on the old-style page.
        { path: 'account/favourites', redirectTo: 'account/saved', pathMatch: 'full' },
        { path: 'account/followed', component: AccountV2Component, data: { title: 'PlateX - Tracked plates', index: false, pane: 'followed' } },
        { path: 'account/registrations', component: AccountV2Component, data: { title: 'PlateX - My Registrations', index: false, pane: 'registrations' } },
        { path: 'account/recent', component: AccountV2Component, data: { title: 'PlateX - Recently viewed', index: false, pane: 'recent' } },
        { path: 'account/updates', component: AccountV2Component, data: { title: 'PlateX - Updates', index: false, pane: 'updates' } },
        { path: 'account/settings', component: AccountV2Component, data: { title: 'PlateX - Settings', index: false, pane: 'settings' } },
        // Admin tools inside the v2 shell; the component bounces non-admins
        // and every tool re-verifies server side.
        { path: 'account/admin', component: AccountV2Component, data: { title: 'PlateX - Admin', index: false, pane: 'admin' } },
      ]
    : [
        // Hosts without account v2 keep the legacy account page. The redesign's
        // Saved / Track buttons write the legacy favourites and notify lists
        // (SavedPlatesService / TrackPlateService), shown in these sections.
        { path: 'saved', component: SavedGuestComponent, data: { title: 'PlateX - Saved plates', index: false } },
        { path: 'account/saved', redirectTo: 'account/favourites', pathMatch: 'full' },
        { path: 'account/followed', redirectTo: 'account/notify-me', pathMatch: 'full' },
      ]),
  // Tokenised email actions (pause / unsubscribe). Must work logged out; the
  // token is the authorisation. Available on every host so email links never
  // dead-end.
  { path: 'alert-action/:token', component: AlertActionComponent, data: {
      title: 'PlateX - Alert settings',
      index: false
    } },
  // Legacy account sections (favourites, notify-me, alerts, messages, details,
  // selling, admin) stay reachable through the parameterised route below on
  // every host until their v2 replacements land.
  { path: 'account/:pageSection', component: AccountComponent, data: {
      index: false
    } },
  { path: 'sellers-hub', component: SellersHubPageComponent, data: {
      index: false
    } },

  // ADVERT
  { path: 'advert-edit/:advertId', component: EditListingPageComponent, data: {
      index: false
    } },
  { path: 'advert-billing/:advertId', component: BillingListingPageComponent, data: {
      index: false
    } },

  // BLOG
  {
    path: 'blog', component: BlogLandingPageComponent, data: {
      title: BLOG_LANDING_TITLE,
      description: BLOG_LANDING_DESCRIPTION,
      index: true,
    }
  },
  {
    // Title/description are set by BlogPageComponent once the post loads.
    path: 'blog/:blogHandle', component: BlogPageComponent, data: {
      title: BLOG_LANDING_TITLE,
      description: BLOG_LANDING_DESCRIPTION,
    }
  },

  {
    path: 'guides',
    component: ArticleLandingComponent,
    data: {
      title: GUIDES_HUB_TITLE,
      description: GUIDES_HUB_DESCRIPTION,
      index: true,
    },
  },
  // One route per published guide (article-landing/guides.ts). The unfinished
  // how-to-purchase-a-registration stub is deliberately not routed.
  ...GUIDES.map((g) => ({
    path: `guides/${g.slug}`,
    component: GUIDE_COMPONENTS[g.slug],
    data: { title: guideTitle(g.name), description: g.description, index: true },
  })),

  // ERROR HANDLING
  // DEV-ONLY live_v2 compare diagnostics (component self-guards on
  // environment.liveV2CompareEnabled; nothing links here in production).
  ...(isDevOnly
    ? [
        { path: 'dev/live-v2-compare', component: LiveV2ComparePageComponent },
        { path: 'dev/live-v2-compare/:registration', component: LiveV2ComparePageComponent },
      ]
    : []),
  // Unknown routes: release hosts show the not-found page (the CDN edge has
  // already answered with HTTP 404); dev keeps its old fallback.
  { path: '**', component: isRelease ? NotFoundComponent : LandingComponent, data: { title: 'Page not found | PlateX', index: false } },
];

@NgModule({
  declarations: [
    AppComponent,
    NotFoundComponent,
    LegacyRedirectComponent,
    LiveAuctionsComponent,
    HomeV2Component,
    SavedGuestComponent,
    HomeSearchComponent,
    HomeMarketComponent,
    HomeInFocusComponent,
    HomeRegistryComponent,
    HomeForYouComponent,
    AuctionAdminComponent,
    PrivacyPolicyComponent,
    DataDeletionComponent,
    CompareResultsComponent,
    HeaderComponent,
    RulesComponent,
    LandingComponent,
    OptionSearchComponent,
    FeaturedPlatesComponent,
    AuctionsComponent,
    CreateAuctionComponent,
    PlateInputComponent,
    RouterButtonComponent,
    SearchResultsPageComponent,
    LiveV2ComparePageComponent,
    PlateModalComponent,
    InfoComponent,
    AccountComponent,
    AccountV2Component,
    SavedSearchBuilderComponent,
    SavedSearchResultsComponent,
    AlertActionComponent,
    FooterComponent,
    LoginPageComponent,
    RegisterComponent,
    ForgotPasswordPageComponent,
    ResetPasswordPageComponent,
    CompareResultTableComponent,
    CheckoutCancelComponent,
    OrderConfirmationComponent,
    BYOSearchPageComponent,
    ListingCardComponent,
    CompareResultRowComponent,
    WildcardCharacterSelectComponent,
    TopBarComponent,
    NavShellComponent,
    AssistantWidgetComponent,
    SearchBodyV2Component,
    FeedbackModalComponent,
    FeedbackDialogModalComponent,
    SpecialOfferSectionComponent,
    SearchingModalComponent,
    AdminPageComponent,
    PlateBreakdownComponent,
    InitialsSearchPageComponent,
    InitialSectionComponent,
    PlatopediaPageComponent,
    PlatopediaListComponent,
    MultiSearchPageComponent,
    NameListPageComponent,
    FavouritesListComponent,
    ResultsHeaderComponent,
    DatelessSearchPageComponent,
    PlateViewerComponent,
    LargeImageModalComponent,
    PlateSellerRowComponent,
    PlateInfoPanelComponent,
    PlateCheckoutStepsComponent,
    NomineeFormComponent,
    ChartComponent,
    PlateOptionSelectionComponent,
    CompareInlineComponent,
    QuickSignUpModalComponent,
    RatingBarComponent,
    SoldDataTableComponent,
    PriceBreakdownComponent,
    SearchVariantBarComponent,
    BannerImageComponent,
    SellingLandingPageComponent,
    TabbedSectionComponent,
    SellersHubPageComponent,
    BuyPageComponent,
    MessengerPageComponent,
    MessengerListComponent,
    MessengerClientComponent,
    SellerTableComponent,
    PlatexDirectComponent,
    ComparePageComponent,
    CompareResultsTableComponent,
    RegistrationDetailPageComponent,
    PlateDetailPageComponent,
    ImageSliderComponent,
    PaymentOptionsComponent,
    PaypalComponent,
    PageStepsComponent,
    HomePageSearchComponent,
    ByoSearchInputComponent,
    DvlaAuctionPageComponent,
    AccountDetailsComponent,
    AccountFavouritesComponent,
    AccountNotifyComponent,
    AccountAlertsComponent,
    AccountMessagesComponent,
    AccountSellingComponent,
    GlossaryComponent,
    GlossaryLinkPipe,
    OrdinalDatePipe,
    OrdinalNumberPipe,
    FixedDigitPipe,
    ImageCachePipe,
    RelativeTimePipe,
    GlossaryDefPipe,
    SafeHtmlPipe,
    GlossaryTermWordComponent,
    SwitchToggleComponent,
    NamePlatePageComponent,
    SignupCaptureModalComponent,
    FaqComponent,
    SpecialPlateInfoComponent,
    EditListingPageComponent,
    BillingListingPageComponent,
    TrackingChartComponent,
    PlateViewerPageComponent,
    BlogPageComponent,
    BlogLandingPageComponent,
    GuidesLibraryComponent,
    GuideLandingComponent,
    GuideReaderComponent,
    WhatsOnYourPlateComponent,
    GuideFeatureComponent,
    SideNavComponent,
    TpSellersComponent,
    SaleDataComponent,
    PriceHistoryComponent,
    CompareLoaderComponent,
    SearchLoaderComponent,
    SideBarFiltersComponent,
    CustomDropdownComponent,
    LoaderComponent,
    MarketInsightComponent,
    DynamicChartsComponent,
    UserHistoryComponent,
    ChartPriceDataComponent,
    NumberPlateTypesComponent,
    PrivateNumberPlatesComponent,
    CherishedNumberPlatesComponent,
    DatelessNumberPlatesComponent,
    PrefixNumberPlatesComponent,
    SuffixNumberPlatesComponent,
    HowToTransferARegistrationComponent,
    HowToRetainARegistrationComponent,
    HowToPurchaseARegistrationComponent,
    HowToSaveMoneyComponent,
    FeaturedBlogsComponent,
    FeaturedListingsComponent,
    ArticleLandingComponent,
    ChartPriceHistoryComponent,
    NewReleasePageComponent,
    PagePlacementComponent,
    NewReleaseMarketDataComponent,
    NamePopularityDataComponent,
    AdminGptComponent,
    AdminWordbankComponent,
    BuyLandingComponent,
    SellLandingComponent,
    PriceHistoryChartComponent,
    CustomLoaderComponent,
    TextChangerComponent,
    NewSearchInputComponent,
    PlateDetailPageComponent,
    FooterPopoutComponent,
    InitialsPopularityDataComponent,
    InitialsPopularityTableComponent,
    MarketShareComponent,
    UnderOneHundredComponent,
    Under200RegisterComponent,
    NumberOnePageComponent,
    NumberOnesRegisterComponent,
    NamesRegisterComponent,
    DatelessRegisterComponent,
    InitialsRegisterComponent,
    DatelessIndexChartComponent,
    DatelessAuthoritiesComponent,
    NameFinderComponent,
    NamesWatchlistComponent,
    NamesPopularityComponent,
    TaxonomyShellComponent,
    SnowComponent,
    AdminBlogCreateComponent,
    AdminBlogConfigComponent,
    AdminStaticMediaComponent,
    CustomLoaderBoxComponent,
    AdminPlateViewerToggleComponent,
    DealerPortalComponent,
    RegistrationMetricsComponent,
    MetricPortalComponent,
    AdminRecentlyAddedComponent,
    AdminUsersComponent,
    AdminFounderCockpitComponent,
    AdminComparablesComponent,
    AdminValuationLabComponent,
    VlabResearchComponent,
    VlabUpcomingComponent,
    AdminDealerHealthComponent,
    MetricGraphComponent,
    MetricGraphCollectionComponent,
    DealerMetricsChartComponent,
    CountUpDirective,
    BasicInputComponent,
    NewReleaseSearchComponent,
    LandingMarketWatchComponent,
    AboutUsPageComponent,
    AboutUsPageV2Component,
    MetricCardComponent,
    PrivacyPolicyPageComponent,
    DataDeletionPageComponent,
    MarketWatchPageComponent,
    AdminSoldSearchComponent,
    AdminEditorialReviewComponent,
    AdminEditorialIntelligenceComponent,
    AdminEditorialWorkbenchComponent,
    AdminEditorialReviewQueueComponent,
    AdminPlatePhotosComponent,
    EditorialImportPanelComponent,
    AdminMultipleSalesComponent,
    AdminYearlyTopSalesComponent,
    SearchTrackingMetricsComponent,
    TabStepperComponent
  ],
  imports: [
    PlateComponentModule,
    SearchResultTableModule,
    // <pla-main-nav-chips> (global nav chip row) + <pla-sticky-tabs>
    // (the marquee chip primitive). Extracted from declarations so
    // feature modules (new-release-page-v2) can also import the chips.
    // Same pattern as PlateComponentModule / SearchResultTableModule.
    MainNavChipsModule,
    // Shared PlateX classification pill (format identity + deep link) —
    // same extraction pattern; PlateDetailV2Module imports it too.
    ClassificationPillModule,
    NgChartsModule,
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule,
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
      onSameUrlNavigation: 'reload',
      anchorScrolling: 'enabled',
      scrollOffset: [0, 0]
    }),
    CodeInputModule.forRoot({}),
    InViewportModule,
    HttpClientModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatSliderModule,
    MatCheckboxModule,
    MatDialogModule,
    MatCardModule,
    MatTooltipModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatTableModule,
    MatBadgeModule,
    MatExpansionModule,
    MatDividerModule,
    MatTabsModule,
    MatProgressBarModule,
    MatSlideToggleModule,
    PlatformModule,
    MatRippleModule,
    SwiperModule,
    MatStepperModule,
    NgChartsModule,
    MatAutocompleteModule,
    MatRadioModule,
    NgxMarqueeModule,
    SellModule,
    NewReleasePageV2Module,
    PlateDetailV2Module,
  ],
  exports: [RouterModule],
  providers: [
    SessionService,
    UserService,
    MatDialogConfig,
    CurrencyPipe,
    DatePipe,
    GlossaryLinkPipe,
    GlossaryDefPipe,
    SafeHtmlPipe,
    DecimalPipe,
    ImageCachePipe,
    RelativeTimePipe,
    // {
    //   provide: ErrorHandler,
    //   useValue: Sentry.createErrorHandler({
    //     showDialog: false,
    //   }),
    // },
    // {
    //   provide: Sentry.TraceService,
    //   deps: [Router],
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => {},
    //   deps: [Sentry.TraceService],
    //   multi: true,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UrlInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccountProfileInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
